import { sourceMethods } from "./source.js";
import { gpxMethods } from "./gpx.js";
import { hotelMethods } from "./hotels.js";
import { searchMethods } from "./search.js";
import { filterMethods } from "./filters.js";
import { gtm } from "../modules/gtm.js";

// Minimum zoom before itineraries appear in the sidebar list
const ITI_LIST_ZOOM = 8;

// Haversine distance in km between two lat/lng points
function haversine(lat1, lng1, lat2, lng2) {
	const R = 6371;
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
	return R * 2 * Math.asin(Math.sqrt(a));
}

class LBHItineraryMap {
	constructor({
		container,
		accessToken,
		style,
		itineraries,
		hotels = [],
		apiBase,
		lang,
		initialItineraryId = null,
		initialMode = "itineraries",
		location = "",
		initialLat = null,
		initialLng = null,
		initialZoom = null,
		initialHasPromo = false,
	}) {
		this.container = container;
		this.accessToken = accessToken;
		this.style = style;
		this.itineraries = itineraries;
		this.hotels = hotels;
		this.apiBase = apiBase;
		this.lang = lang || "";
		this.location = location;
		this.initialLat = initialLat;
		this.initialLng = initialLng;
		this.initialZoom = initialZoom;
		this.map = null;
		this.mode = "itineraries";
		this.initialMode = initialMode;
		this.activeId = null;
		this.activeHotelId = null;
		this._hotelMarkers = []; // nearby hotel markers (itinerary detail)
		this._allHotelMarkers = []; // all hotel markers (hotel mode)
		this._activePinnedHotelId = null;
		this._nearbyItiMarkersAdded = false; // itinerary pins shown in hotel detail
		this._nearbyGPXLayerIds = []; // GPX track layers shown in hotel detail
		this._nearbyGPXSession = 0; // session counter to abort stale GPX loads
		this.initialItineraryId = initialItineraryId;
		this.initialHasPromo = initialHasPromo;
		this._selectedItiId = null;
		this._renderItineraryList = null;
		this._renderHotelAllList = null;
		this._resizeHandler = null;
		this._splideGallery = null;

		this.sidebarWidth = 360;

		this.filters = {
			durationMin: -Infinity,
			durationMax: Infinity,
			distanceMin: -Infinity,
			distanceMax: Infinity,
			climbMin: -Infinity,
			climbMax: Infinity,
			difficulty: 0,
			bikeTypes: [],
		};

		this.hotelFilters = {
			hasPromo: false,
			rating: 0,
		};
	}

	_getInitialCamera() {
		const presets = {
			north: { center: [11.0, 45.5], zoom: 7.2 },
			center: { center: [12.5, 42.5], zoom: 7.2 },
			south: { center: [15.8, 40.0], zoom: 7.0 },
		};
		const base = presets[this.location] || { center: [12.5, 42.5], zoom: 5.2 };
		const center =
			this.initialLat !== null && this.initialLng !== null ? [this.initialLng, this.initialLat] : base.center;
		const zoom = this.initialZoom !== null ? this.initialZoom : base.zoom;
		return { center, zoom };
	}

	_getSidebarPadding() {
		const isMobile = window.innerWidth <= 768;
		if (isMobile) {
			const sidebar = document.getElementById("map-sidebar");
			if (!sidebar) return { left: 0, top: 0, right: 0, bottom: 40 };
			// When collapsed only the handle (always 40 px) is visible;
			// when expanded read the live height so it works on every device.
			const bottom = sidebar.classList.contains("is-collapsed")
				? 40
				: sidebar.offsetHeight;
			return { left: 0, top: 0, right: 0, bottom };
		}
		return { left: this.sidebarWidth, top: 0, right: 0, bottom: 0 };
	}

	init() {
		/* global mapboxgl */
		mapboxgl.accessToken = this.accessToken;

		const { center, zoom } = this._getInitialCamera();

		this.map = new mapboxgl.Map({
			container: this.container,
			style: this.style,
			center,
			zoom,
			minZoom: 4,
		});

		this.map.addControl(new mapboxgl.NavigationControl(), "top-right");

		this.map.on("load", () => {
			this.map.setPadding(this._getSidebarPadding());

			this._loadMapImages(() => {
				this._addSource();
				this._addLayers();
				this._bindMapEvents();
				this.initFilters();
				this.initHotelFilters();
				this._initSearch();
				this._initItineraryList();
				this._initHotelAllList();
				this._initModeTabs();
				this._switchMode(this.initialMode);
				this._openInitialItinerary();
			});
		});

		// Keep Mapbox padding in sync with sidebar when window resizes
		this._resizeHandler = () => {
			if (this.map) this.map.setPadding(this._getSidebarPadding());
		};
		window.addEventListener("resize", this._resizeHandler);
	}

	// ── Mode switching ──────────────────────────────────────────────────────

	_initModeTabs() {
		document.querySelectorAll(".sidebar-tab").forEach((tab) => {
			tab.addEventListener("click", () => {
				if (tab.dataset.mode === this.mode) return;
				this._switchMode(tab.dataset.mode);
			});
		});
	}

	_switchMode(mode) {
		const prevMode = this.mode;
		this.mode = mode;

		// Close detail panel if open
		const detail = document.getElementById("sidebar-detail");
		const filtersPl = document.getElementById("sidebar-filters");
		if (detail && !detail.classList.contains("hidden")) {
			detail.classList.add("hidden");
			filtersPl?.classList.remove("hidden");
		}

		// Clean up previous mode state
		if (prevMode === "itineraries") {
			this._removeGPXLayer?.();
			this._clearHotelMarkers?.();
			this.activeId = null;
			this._highlightItinerary(null);
			if (this._splideGallery) {
				this._splideGallery.destroy();
				this._splideGallery = null;
			}
		} else if (prevMode === "accommodations") {
			this.activeHotelId = null;
			this._restoreItiSections();
			this._clearNearbyItiMarkers();
		}

		// Update tab active state
		document.querySelectorAll(".sidebar-tab").forEach((tab) => {
			tab.classList.toggle("is-active", tab.dataset.mode === mode);
		});

		const itiPanel = document.getElementById("iti-filters-panel");
		const hotelPanel = document.getElementById("hotel-mode-panel");

		if (mode === "accommodations") {
			if (itiPanel) itiPanel.hidden = true;
			if (hotelPanel) hotelPanel.hidden = false;

			// Hide itinerary layers
			["clusters", "cluster-count", "unclustered-point"].forEach((layer) => {
				this.map.setLayoutProperty(layer, "visibility", "none");
			});

			// Show all hotel markers and render list
			this._initAllHotelMarkers();
			if (this._renderHotelAllList) this._renderHotelAllList("");
		} else {
			if (itiPanel) itiPanel.hidden = false;
			if (hotelPanel) hotelPanel.hidden = true;

			// Remove all hotel markers from hotel mode
			this._clearAllHotelMarkers();

			// Show itinerary layers
			["clusters", "cluster-count", "unclustered-point"].forEach((layer) => {
				this.map.setLayoutProperty(layer, "visibility", "visible");
			});

			// Re-apply itinerary filters and refresh list
			this._applyFilters();
			const searchEl = document.getElementById("iti-search");
			if (searchEl) searchEl.value = "";
			if (this._renderItineraryList) this._renderItineraryList("");
		}
	}

	// ── Itinerary detail ────────────────────────────────────────────────────

	_openItinerary(feature) {
		const { id, title, gpx_url } = feature.properties;
		const [lng, lat] = feature.geometry.coordinates;
		this.activeId = id;

		this.map.setFilter("unclustered-point", ["==", ["get", "id"], id]);
		this.map.setFilter("clusters", ["literal", false]);
		this.map.setFilter("cluster-count", ["literal", false]);

		document.getElementById("sidebar-filters").classList.add("hidden");
		const detail = document.getElementById("sidebar-detail");
		detail.classList.remove("hidden");
		detail.scrollTop = 0;
		const _scrollBody = document.getElementById("sidebar-scroll-body");
		if (_scrollBody) _scrollBody.scrollTop = 0;

		// Ensure iti-specific sections are visible, hotel-specific hidden
		this._restoreItiSections();
		document.getElementById("hotel-detail-info")?.setAttribute("hidden", "");
		document.getElementById("nearby-iti-list-wrap")?.setAttribute("hidden", "");

		const iti = this.itineraries.find((i) => i.id === parseInt(id, 10) || i.id === id);
		const s = window.lbhMapStrings || {};

		document.getElementById("detail-title").innerHTML = title;

		const tagsEl = document.getElementById("detail-tags");
		if (tagsEl) {
			const tags = [];
			if (iti?.region) tags.push(iti.region);
			iti?.bike_types?.forEach((bt) => tags.push(bt));
			tagsEl.innerHTML = tags.map((t) => `<span class="detail-tag">${t}</span>`).join("");
		}

		const descEl = document.getElementById("detail-description");
		if (descEl) descEl.innerHTML = iti?.description || "";

		const statsEl = document.getElementById("detail-stats");
		if (statsEl && iti) {
			const diff = iti.difficulty || 0;
			const diffLabels = s.diffLabels || ["", "Facile", "Moderato", "Medio", "Difficile", "Molto Difficile"];
			const dots = Array.from(
				{ length: 5 },
				(_, i) => `<span class="dot${i < diff ? " is-filled" : ""}"></span>`,
			).join("");
			statsEl.innerHTML = `
				<div class="detail-stat">
					<span class="detail-stat__label">${s.difficulty || "Difficoltà"}</span>
					<div class="detail-stat__dots">${dots}</div>
					${diff ? `<span class="detail-stat__sub">${diffLabels[diff] || ""}</span>` : ""}
				</div>
				${
					iti.distance
						? `<div class="detail-stat">
					<span class="detail-stat__label">${s.distance || "Distanza"}</span>
					<span class="detail-stat__value">${iti.distance} km</span>
				</div>`
						: ""
				}
				${
					iti.duration
						? `<div class="detail-stat">
					<span class="detail-stat__label">${s.duration || "Durata"}</span>
					<span class="detail-stat__value">${iti.duration} h</span>
				</div>`
						: ""
				}
				${
					iti.climb
						? `<div class="detail-stat">
					<span class="detail-stat__label">${s.climb || "Dislivello"}</span>
					<span class="detail-stat__value">${iti.climb} m</span>
				</div>`
						: ""
				}`;
		}

		// Gallery
		if (this._splideGallery) {
			this._splideGallery.destroy();
			this._splideGallery = null;
		}
		const galleryEl = document.getElementById("detail-gallery");
		const galleryList = document.getElementById("detail-gallery-list");
		const gallery = iti?.gallery || [];
		if (gallery.length && galleryEl && galleryList) {
			galleryList.innerHTML = gallery
				.map(
					(img) =>
						`<li class="splide__slide">
					<img src="${img.url}" alt="${img.alt || ""}" class="detail-gallery__img">
				</li>`,
				)
				.join("");
			galleryEl.style.display = "";
			/* global Splide */
			this._splideGallery = new Splide(galleryEl, {
				type: "loop",
				perPage: 1,
				arrows: false,
				pagination: true,
				rewind: true,
			});
			this._splideGallery.mount();
		} else if (galleryEl) {
			galleryEl.style.display = "none";
		}

		// Portare description
		const portareWrap = document.getElementById("detail-portare-wrap");
		const portareDesc = document.getElementById("detail-portare-desc");
		if (portareWrap && portareDesc) {
			if (iti?.portare_description) {
				portareDesc.innerHTML = iti.portare_description;
				portareWrap.style.display = "";
			} else {
				portareDesc.innerHTML = "";
				portareWrap.style.display = "none";
			}
		}

		const chartWrap = document.getElementById("elevation-chart-wrap");
		chartWrap.innerHTML = `<p class="chart-loading">${s.chartLoading || "Caricamento tracciato..."}</p>`;
		if (gpx_url) this._loadGPX(gpx_url);
		else chartWrap.innerHTML = "";

		this._fetchNearbyHotels(lat, lng);
	}

	// ── Hotel detail (hotel mode) ───────────────────────────────────────────

	_openHotelDetail(hotel) {
		gtm.clickHotelMap( { hotel_id: hotel.id, hotel_name: hotel.title, hotel_region: hotel.region || '' } );
		this.activeHotelId = hotel.id;

		document.getElementById("sidebar-filters").classList.add("hidden");
		const detail = document.getElementById("sidebar-detail");
		detail.classList.remove("hidden");
		detail.scrollTop = 0;
		const _scrollBody = document.getElementById("sidebar-scroll-body");
		if (_scrollBody) _scrollBody.scrollTop = 0;

		// Hide iti-specific sections
		["detail-stats", "detail-gallery", "detail-portare-wrap", "elevation-chart-wrap", "hotel-list-wrap"].forEach(
			(id) => {
				const el = document.getElementById(id);
				if (el) el.style.display = "none";
			},
		);

		// Show hotel-specific sections
		document.getElementById("hotel-detail-info")?.removeAttribute("hidden");
		document.getElementById("nearby-iti-list-wrap")?.removeAttribute("hidden");

		// Tags + title
		const s = window.lbhMapStrings || {};
		const tagsEl = document.getElementById("detail-tags");
		if (tagsEl) {
			tagsEl.innerHTML = hotel.region ? `<span class="detail-tag">${hotel.region}</span>` : "";
		}
		document.getElementById("detail-title").textContent = hotel.title;
		const descEl = document.getElementById("detail-description");
		if (descEl) descEl.textContent = "";

		// Rating wheels
		const ratingEl = document.getElementById("hotel-rating-display");
		if (ratingEl) {
			if (hotel.rating) {
				const wheels = Array.from(
					{ length: 5 },
					(_, i) => `<span class="dot${i < hotel.rating ? " is-filled" : ""}"></span>`,
				).join("");
				ratingEl.innerHTML = `<div class="detail-stat__dots hotel-rating-dots">${wheels}</div>`;
			} else {
				ratingEl.innerHTML = "";
			}
		}

		// Promo badge
		const promoBadge = document.getElementById("hotel-promo-badge");
		if (promoBadge) promoBadge.hidden = !hotel.has_promo;

		// Permalink CTA
		const permalinkEl = document.getElementById("hotel-permalink");
		if (permalinkEl) {
			permalinkEl.href = hotel.permalink;
			permalinkEl.textContent = s.visitHotel || "Visita la struttura";
		}

		// Nearby itineraries
		this._renderNearbyItineraries(hotel.lat, hotel.lng);

		// Show only active marker, hide all others; popups appear on hover only
		this._allHotelMarkers.forEach(({ id, el: markerEl, popup }) => {
			popup?.remove();
			if (id === hotel.id) {
				if (markerEl) {
					markerEl.style.display = "";
					markerEl.classList.add("is-active");
				}
			} else {
				if (markerEl) {
					markerEl.style.display = "none";
					markerEl.classList.remove("is-active");
				}
			}
		});

		this.map.flyTo({ center: [hotel.lng, hotel.lat], zoom: 12, duration: 800 });
	}

	// ── Reset ───────────────────────────────────────────────────────────────

	resetMap() {
		if (this.mode === "accommodations") {
			this._resetHotelDetail();
		} else {
			this._resetItineraryDetail();
		}
	}

	_resetItineraryDetail() {
		this.activeId = null;

		this.map.stop();
		this._removeGPXLayer();
		this._clearHotelMarkers();
		this._highlightItinerary(null);

		// Restore original layer filters
		this.map.setFilter("unclustered-point", ["!", ["has", "point_count"]]);
		this.map.setFilter("clusters", ["has", "point_count"]);
		this.map.setFilter("cluster-count", ["has", "point_count"]);

		this._applyFilters();

		if (this._splideGallery) {
			this._splideGallery.destroy();
			this._splideGallery = null;
		}

		document.getElementById("sidebar-detail").classList.add("hidden");
		document.getElementById("sidebar-filters").classList.remove("hidden");
		document.getElementById("elevation-chart-wrap").innerHTML = "";
		document.getElementById("hotel-list").innerHTML = "";

		const galleryEl = document.getElementById("detail-gallery");
		const portareWrap = document.getElementById("detail-portare-wrap");
		if (galleryEl) galleryEl.style.display = "none";
		if (portareWrap) portareWrap.style.display = "none";

		const searchEl = document.getElementById("iti-search");
		if (searchEl) searchEl.value = "";
		if (this._renderItineraryList) this._renderItineraryList("");
	}

	_resetHotelDetail() {
		this.activeHotelId = null;

		document.getElementById("sidebar-detail").classList.add("hidden");
		document.getElementById("sidebar-filters").classList.remove("hidden");

		// Clear nearby itinerary markers and GPX tracks
		this._clearNearbyItiMarkers();

		// Restore iti-specific section display for future use
		this._restoreItiSections();

		// Hide hotel-specific sections
		document.getElementById("hotel-detail-info")?.setAttribute("hidden", "");
		document.getElementById("nearby-iti-list-wrap")?.setAttribute("hidden", "");

		// Restore all markers and remove popups, then re-apply filters
		this._allHotelMarkers.forEach(({ el: markerEl, popup }) => {
			if (markerEl) markerEl.classList.remove("is-active");
			if (popup) popup.remove();
		});
		this._applyHotelFilters();

		// Re-render hotel list for current viewport
		const searchEl = document.getElementById("hotel-search");
		if (searchEl) searchEl.value = "";
		if (this._renderHotelAllList) this._renderHotelAllList("");
	}

	// ── Itinerary highlight (map ↔ sidebar sync) ────────────────────────────

	_highlightItinerary(id) {
		this._selectedItiId = id;

		// Emphasise the selected pin on the map via a data-driven icon-size expression
		if (this.map.getLayer("unclustered-point")) {
			this.map.setLayoutProperty(
				"unclustered-point",
				"icon-size",
				id != null ? ["case", ["==", ["get", "id"], id], 1.35, 1.0] : 1.0,
			);
		}

		// Sync sidebar card: remove previous selection, add to current
		document.querySelectorAll("[data-iti-id].is-selected").forEach((el) => el.classList.remove("is-selected"));
		if (id != null) {
			const card = document.querySelector(`[data-iti-id="${id}"]`);
			if (card) {
				card.classList.add("is-selected");
				card.scrollIntoView({ behavior: "smooth", block: "nearest" });
			}
		}
	}

	_restoreItiSections() {
		["detail-stats", "detail-gallery", "detail-portare-wrap", "elevation-chart-wrap", "hotel-list-wrap"].forEach(
			(id) => {
				const el = document.getElementById(id);
				if (el) el.style.display = "";
			},
		);
	}

	// ── Itinerary list ──────────────────────────────────────────────────────

	_initItineraryList() {
		const listEl = document.getElementById("iti-list");
		const searchEl = document.getElementById("iti-search");
		if (!listEl || !searchEl) return;

		const render = (query = "") => {
			if (this.mode !== "itineraries") return;
			const s = window.lbhMapStrings || {};
			const zoom = this.map.getZoom();

			if (zoom < ITI_LIST_ZOOM) {
				listEl.innerHTML = `<li class="iti-zoom-hint">${s.zoomToSeeList || "Avvicina la mappa per vedere gli itinerari."}</li>`;
				return;
			}

			const bounds = this.map.getBounds();
			const center = this.map.getCenter();
			const q = query.trim().toLowerCase();

			const visible = this.itineraries
				.filter((iti) => {
					if (!bounds.contains([iti.lng, iti.lat])) return false;
					if (q && !iti.title.toLowerCase().includes(q)) return false;
					return true;
				})
				.map((iti) => ({
					...iti,
					_dist: haversine(center.lat, center.lng, iti.lat, iti.lng),
				}))
				.sort((a, b) => a._dist - b._dist);

			if (!visible.length) {
				listEl.innerHTML = `<li class="iti-empty">${s.noItineraries || "Nessun itinerario trovato."}</li>`;
				return;
			}

			listEl.innerHTML = visible
				.map((iti) => {
					const diff = iti.difficulty || 0;
					const dots = Array.from(
						{ length: 5 },
						(_, i) => `<span class="dot${i < diff ? " is-filled" : ""}"></span>`,
					).join("");
					const tags = [];
					if (iti.region) tags.push(iti.region);
					iti.bike_types?.forEach((bt) => tags.push(bt));

					const hasExtra = iti.description || iti.duration || iti.climb || iti.permalink;

					return `
					<li class="iti-card" data-iti-id="${iti.id}">
						<div class="iti-card__main">
							${
								iti.thumbnail
									? `<img src="${iti.thumbnail}" alt="${iti.title}" class="iti-card__img">`
									: '<span class="iti-card__img iti-card__img--empty"></span>'
							}
							<div class="iti-card__body">
								${tags.length ? `<div class="iti-card__tags">${tags.map((t) => `<span class="detail-tag">${t}</span>`).join("")}</div>` : ""}
								<strong class="iti-card__title">${iti.title}</strong>
								<div class="iti-card__meta">
									<div class="iti-card__dots">${dots}</div>
									${iti.distance ? `<span class="iti-card__dist">${iti.distance} km</span>` : ""}
									${hasExtra ? `<button class="iti-card__expand" aria-label="${s.expandDetails || "Espandi dettagli"}" aria-expanded="false">+</button>` : ""}
								</div>
							</div>
						</div>
						${
							hasExtra
								? `
						<div class="iti-card__extra" hidden>
							${iti.description ? `<p class="iti-card__desc">${iti.description}</p>` : ""}
							${
								iti.duration || iti.climb
									? `
							<div class="iti-card__extra-stats">
								${iti.duration ? `<span class="iti-card__extra-stat"><strong>${s.duration || "Durata"}:</strong> ${iti.duration} h</span>` : ""}
								${iti.climb ? `<span class="iti-card__extra-stat"><strong>${s.climb || "Dislivello"}:</strong> ${iti.climb} m</span>` : ""}
							</div>`
									: ""
							}
						</div>`
								: ""
						}
					</li>`;
				})
				.join("");

			visible.forEach((iti) => {
				const card = listEl.querySelector(`[data-iti-id="${iti.id}"]`);
				if (!card) return;

				card.addEventListener("click", () => {
					gtm.clickItineraryCard( {
						itinerary_id         : iti.id,
						itinerary_name       : iti.title,
						itinerary_region     : iti.region || '',
						itinerary_difficulty : iti.difficulty || 0,
					} );
					this._highlightItinerary(iti.id);
					this._openItinerary({
						properties: { id: iti.id, title: iti.title, gpx_url: iti.gpx_url },
						geometry: { coordinates: [iti.lng, iti.lat] },
					});
					this.map.flyTo({ center: [iti.lng, iti.lat], zoom: 10 });
				});

				const expandBtn = card.querySelector(".iti-card__expand");
				if (expandBtn) {
					expandBtn.addEventListener("click", (e) => {
						e.stopPropagation();
						const extra = card.querySelector(".iti-card__extra");
						const isExpanded = !extra.hidden;
						extra.hidden = isExpanded;
						expandBtn.textContent = isExpanded ? "+" : "−";
						expandBtn.setAttribute("aria-expanded", String(!isExpanded));
						// Sync map: highlight on expand, clear on collapse
						this._highlightItinerary(isExpanded ? null : iti.id);
					});
				}

				const extraEl = card.querySelector(".iti-card__extra");
				if (extraEl) extraEl.addEventListener("click", (e) => e.stopPropagation());
			});

			// Re-apply selected state after each list re-render (map pan/zoom)
			if (this._selectedItiId != null) {
				const sel = listEl.querySelector(`[data-iti-id="${this._selectedItiId}"]`);
				if (sel) sel.classList.add("is-selected");
			}
		};

		this._renderItineraryList = render;
		render();

		// Update list whenever the map viewport changes
		this.map.on("moveend", () => {
			if (this.mode !== "itineraries") return;
			if (this.activeId) return;
			render(searchEl.value);
		});

		searchEl.addEventListener("input", () => render(searchEl.value));
	}

	// ── Map image loading ───────────────────────────────────────────────────

	_loadMapImages(callback) {
		const svg = `<svg width="44" height="55" viewBox="0 0 44 55" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_6301_2455)">
    <path d="M22 1C33.5756 1 43 10.5202 43 22.2607C42.9998 29.6628 39.1645 37.1577 32.5703 44.4814C30.3474 46.9456 27.9587 49.2497 25.4219 51.3779L25.4131 51.3857C24.5849 52.0969 23.7801 52.7338 23.0869 53.2793C22.7293 53.5348 22.3838 53.7794 22.1758 53.9453C22.1226 53.9807 22.0627 53.9999 22.002 54C21.9405 54 21.8798 53.9804 21.8262 53.9443C21.6162 53.777 21.2664 53.5309 20.9062 53.2734C20.2151 52.7295 19.4144 52.0938 18.5898 51.3857L18.5811 51.3789L17.6367 50.5723C15.4491 48.6712 13.3772 46.6363 11.4316 44.4795H11.4307C4.83349 37.1579 1.00017 29.661 1 22.2607C1 10.5202 10.4244 1 22 1Z" fill="#A8BEBD" stroke="#062423" stroke-width="2"/>
    <path d="M27.9844 17.6118C26.6819 17.6118 25.623 16.5773 25.623 15.3059C25.623 14.0345 26.6831 13 27.9844 13C29.2857 13 30.3457 14.0345 30.3457 15.3059C30.3457 16.5773 29.2869 17.6118 27.9844 17.6118ZM27.9844 14.1729C27.3445 14.1729 26.8235 14.6808 26.8235 15.3059C26.8235 15.9311 27.3445 16.4389 27.9844 16.4389C28.6242 16.4389 29.1452 15.9311 29.1452 15.3059C29.1452 14.6808 28.6242 14.1729 27.9844 14.1729Z" fill="#062423"/>
    <path d="M14.0288 32.5312C11.2557 32.5312 9 30.3273 9 27.6191C9 24.9109 11.2557 22.707 14.0288 22.707C16.8018 22.707 19.0587 24.9109 19.0587 27.6191C19.0587 30.3273 16.803 32.5312 14.0288 32.5312ZM14.0288 23.8811C11.9171 23.8811 10.2005 25.5583 10.2005 27.6203C10.2005 29.6822 11.9183 31.3595 14.0288 31.3595C16.1392 31.3595 17.8583 29.6822 17.8583 27.6203C17.8583 25.5583 16.1404 23.8811 14.0288 23.8811Z" fill="#062423"/>
    <path d="M28.9714 32.5312C26.1983 32.5312 23.9414 30.3273 23.9414 27.6191C23.9414 24.9109 26.1971 22.707 28.9714 22.707C31.7457 22.707 34.0001 24.9109 34.0001 27.6191C34.0001 30.3273 31.7445 32.5312 28.9714 32.5312ZM28.9714 23.8811C26.8597 23.8811 25.1419 25.5583 25.1419 27.6203C25.1419 29.6822 26.8597 31.3595 28.9714 31.3595C31.083 31.3595 32.7997 29.6822 32.7997 27.6203C32.7997 25.5583 31.0818 23.8811 28.9714 23.8811Z" fill="#062423"/>
    <path d="M22.1231 29.7723C21.8002 29.7723 21.5337 29.5213 21.5229 29.2034L21.3476 23.4469L17.625 19.2655C17.4905 19.1142 17.4437 18.9054 17.5025 18.7131C17.5613 18.5207 17.7174 18.3706 17.9155 18.3166L23.9166 16.6664C24.1387 16.6054 24.3764 16.6746 24.5301 16.8435L27.0967 19.6889L30.3799 19.4942C30.7101 19.4743 30.9958 19.7206 31.015 20.0443C31.0354 20.368 30.7833 20.646 30.452 20.6648L26.8758 20.8771C26.6909 20.8888 26.5108 20.8149 26.3896 20.68L23.8782 17.8967L19.152 19.1963L22.3932 22.837C22.4845 22.939 22.5361 23.0692 22.5409 23.2041L22.7233 29.1706C22.733 29.4943 22.4724 29.7641 22.1411 29.7746C22.1351 29.7746 22.1291 29.7746 22.1231 29.7746V29.7723Z" fill="#062423"/>
  </g>
  <defs>
    <clipPath id="clip0_6301_2455">
      <rect width="44" height="55" fill="white"/>
    </clipPath>
  </defs>
</svg>`;

		const img = new Image(44, 55);
		img.onload = () => {
			this.map.addImage("itinerary-pin", img);
			callback();
		};
		img.onerror = () => callback();
		img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
	}

	_openInitialItinerary() {
		if (!this.initialItineraryId) return;
		const iti = this.itineraries.find((i) => i.id === this.initialItineraryId);
		if (!iti) return;

		this._openItinerary({
			properties: { id: iti.id, title: iti.title, gpx_url: iti.gpx_url },
			geometry: { coordinates: [iti.lng, iti.lat] },
		});
		this.map.flyTo({ center: [iti.lng, iti.lat], zoom: 10 });
	}
}

Object.assign(LBHItineraryMap.prototype, sourceMethods, gpxMethods, hotelMethods, searchMethods, filterMethods);

export default LBHItineraryMap;
